import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Code, Cloud, Shield, Cpu, Brain, BarChart3, 
  Link as LinkIcon, Gamepad2, Bot, Rocket, ChevronRight,
  Sparkles, Zap, Server, Database, Lock, CircuitBoard,
  Smartphone, Globe, Cpu as Cpu2, LineChart, Terminal
} from 'lucide-react';

const serviceIcons = {
  'software-development': { icon: Code, color: 'from-blue-500 to-cyan-500' },
  'cloud-computing': { icon: Cloud, color: 'from-indigo-500 to-purple-500' },
  'cyber-security': { icon: Shield, color: 'from-red-500 to-orange-500' },
  'vlsi-design': { icon: CircuitBoard, color: 'from-green-500 to-emerald-500' },
  'ai-ml-solutions': { icon: Brain, color: 'from-pink-500 to-rose-500' },
  'data-analytics': { icon: BarChart3, color: 'from-amber-500 to-yellow-500' },
  'blockchain': { icon: LinkIcon, color: 'from-violet-500 to-purple-500' },
  'game-development': { icon: Gamepad2, color: 'from-fuchsia-500 to-pink-500' },
  'ai-automation': { icon: Bot, color: 'from-cyan-500 to-blue-500' },
  'startup-mvp': { icon: Rocket, color: 'from-orange-500 to-red-500' },
  'vlsi': { icon: Cpu2, color: 'from-teal-500 to-green-500' },
};

const serviceDescriptions = {
  'software-development': 'Custom software solutions for web, mobile, and desktop',
  'cloud-computing': 'AWS, Azure, Google Cloud solutions for scalability',
  'cyber-security': 'Complete security solutions including penetration testing',
  'vlsi-design': 'Semiconductor and chip design services',
  'ai-ml-solutions': 'AI and Machine Learning solutions for business',
  'data-analytics': 'Data analysis and business intelligence services',
  'blockchain': 'Blockchain development and Web3 solutions',
  'game-development': '2D/3D game development across platforms',
  'ai-automation': 'Automate processes with intelligent AI systems',
  'startup-mvp': 'Minimum Viable Product development for startups',
  'vlsi': 'Comprehensive VLSI design and verification services',
};

const RelatedServices = ({ currentService = null, maxItems = 6 }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const allServices = [
    { slug: 'software-development', name: 'Software Development', priority: 1 },
    { slug: 'cloud-computing', name: 'Cloud Computing', priority: 2 },
    { slug: 'cyber-security', name: 'Cyber Security', priority: 3 },
    { slug: 'ai-ml-solutions', name: 'AI & ML Solutions', priority: 4 },
    { slug: 'data-analytics', name: 'Data Analytics', priority: 5 },
    { slug: 'vlsi-design', name: 'VLSI Design', priority: 6 },
    { slug: 'blockchain', name: 'Blockchain & Web3', priority: 7 },
    { slug: 'game-development', name: 'Game Development', priority: 8 },
    { slug: 'ai-automation', name: 'AI Automation', priority: 9 },
    { slug: 'startup-mvp', name: 'Startup MVP', priority: 10 },
    { slug: 'vlsi', name: 'VLSI Services', priority: 11 },
  ];

  // Filter out current service and sort by priority
  const relatedServices = currentService 
    ? allServices
        .filter(service => service.slug !== currentService)
        .sort((a, b) => a.priority - b.priority)
    : allServices.sort((a, b) => a.priority - b.priority);

  // Take only maxItems
  const displayServices = relatedServices.slice(0, maxItems);

  // Group services for mobile carousel view
  const groupedServices = isMobile 
    ? [displayServices.slice(0, 2), displayServices.slice(2, 4), displayServices.slice(4, 6)]
    : null;

  // Carousel state for mobile
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % (groupedServices?.length || 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + (groupedServices?.length || 1)) % (groupedServices?.length || 1));
  };

  return (
    <section className="mt-12 lg:mt-16">
      {/* Section Header */}
      <div className="text-center mb-8 lg:mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mb-4">
          <Sparkles className="w-4 h-4 text-white" />
          <span className="text-sm font-semibold text-white tracking-wide">
            COMPLETE SOLUTIONS
          </span>
        </div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 lg:mb-4 px-4">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Explore Our Services
          </span>
        </h2>
        <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto px-4 leading-relaxed">
          Discover comprehensive technology solutions designed to transform your business and drive innovation
        </p>
      </div>

      {/* Desktop Grid View */}
      {!isMobile && (
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {displayServices.map((service) => {
            const { icon: Icon, color } = serviceIcons[service.slug] || { icon: Code, color: 'from-blue-500 to-cyan-500' };
            const description = serviceDescriptions[service.slug] || 'Professional technology solutions';
            
            return (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="group relative"
                onMouseEnter={() => setHoveredService(service.slug)}
                onMouseLeave={() => setHoveredService(null)}
              >
                <div className="h-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:border-transparent hover:bg-gradient-to-br hover:from-white hover:to-gray-50 dark:hover:from-gray-800 dark:hover:to-gray-900 group-hover:translate-y-[-4px]">
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-2xl from-blue-500 to-purple-600" />
                  
                  {/* Icon */}
                  <div className="mb-5">
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${color} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                        {service.name}
                      </h3>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                      {description}
                    </p>

                    {/* Progress Indicator */}
                    <div className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${color} transition-all duration-700 ease-out`}
                        style={{ 
                          width: hoveredService === service.slug ? '100%' : '0%',
                          transitionDelay: hoveredService === service.slug ? '0ms' : '300ms'
                        }}
                      />
                    </div>

                    {/* CTA Button */}
                    <div className="mt-5">
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 dark:text-blue-400 group-hover:gap-2 transition-all duration-300">
                        Explore Service
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Mobile Carousel View */}
      {isMobile && groupedServices && (
        <div className="md:hidden">
          <div className="relative">
            {/* Carousel Container */}
            <div className="overflow-hidden rounded-2xl">
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {groupedServices.map((group, groupIndex) => (
                  <div key={groupIndex} className="w-full flex-shrink-0 px-4">
                    <div className="space-y-4">
                      {group.map((service) => {
                        const { icon: Icon, color } = serviceIcons[service.slug] || { icon: Code, color: 'from-blue-500 to-cyan-500' };
                        const description = serviceDescriptions[service.slug] || 'Professional technology solutions';
                        
                        return (
                          <Link
                            key={service.slug}
                            to={`/services/${service.slug}`}
                            className="block bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 p-5 active:scale-[0.98] transition-all duration-300"
                          >
                            <div className="flex items-start gap-4">
                              <div className={`p-3 rounded-lg bg-gradient-to-br ${color}`}>
                                <Icon className="w-6 h-6 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <h3 className="font-bold text-gray-900 dark:text-white">
                                    {service.name}
                                  </h3>
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  {description}
                                </p>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carousel Navigation */}
            <div className="flex items-center justify-between mt-6 px-4">
              <div className="flex items-center gap-2">
                {groupedServices.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-blue-600 w-6' 
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={prevSlide}
                  className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Previous services"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                </button>
                <button
                  onClick={nextSlide}
                  className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Next services"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View All Services Button */}
      <div className="text-center mt-10 lg:mt-12">
        <Link
          to="/services"
          className="group inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 font-semibold text-sm sm:text-base"
        >
          <Zap className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
          <span>View All Services</span>
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
        
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-500">
          <span className="font-semibold text-blue-600 dark:text-blue-400">12+ Services</span> • <span className="font-semibold text-purple-600 dark:text-purple-400">50+ Projects</span> • <span className="font-semibold text-green-600 dark:text-green-400">15+ Experts</span>
        </p>
      </div>

      {/* Quick Stats Bar */}
      <div className="mt-12 p-6 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-3">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">12+</div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Services</div>
          </div>
          <div className="p-3 border-x border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">50+</div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Projects</div>
          </div>
          <div className="p-3">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">15+</div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Experts</div>
          </div>
        </div>
      </div>

      {/* Service Categories */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
          Popular Categories
        </h3>
        <div className="flex flex-wrap justify-center gap-2">
          {['Software', 'Cloud', 'AI/ML', 'Security', 'Data', 'VLSI'].map((category) => (
            <span
              key={category}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm rounded-full font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300"
            >
              {category}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Performance optimized version
export default React.memo(RelatedServices);